import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EmployerLayout } from '../JobPostPricing/EmployerLayout';
import { AssistantPage } from '../Applications/copilot/AssistantPage';
import { cn } from '@/lib/utils';
import { useCompanyAccess } from '../../Context/CompanyAccessContext';
import { SETTINGS_TABS } from './settingsTabs';

/**
 * Unified Settings shell — a horizontal top-tab secondary nav + content area.
 *
 * We use TOP TABS (not a left rail) on purpose: the app already has a
 * vertical primary sidebar, so a second vertical rail read as "two sidebars
 * open at once". Horizontal section tabs keep a single vertical nav while
 * still showing every settings area and the current one — the GitHub /
 * Vercel org-settings pattern.
 */
export function SettingsLayout({ activeKey, title, description, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { can, teamEnabled } = useCompanyAccess();

  const tabs = SETTINGS_TABS.filter((tab) => can(tab.permission) && (!tab.needsTeam || teamEnabled));

  const isActive = (item) =>
    item.key === activeKey ||
    item.matches.some((m) => location.pathname.startsWith(m));

  return (
    <EmployerLayout>
      <AssistantPage page="settings" />
      <div className="flex flex-col w-full max-w-4xl mx-auto">
        {/* Page header */}
        <div className="mb-5">
          <nav className="text-xs text-muted-foreground mb-1" aria-label="Breadcrumb">
            Settings
          </nav>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            {title || 'Settings'}
          </h1>
          {description && (
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          )}
        </div>

        {/* Section tabs — horizontal, underline-active. Scrolls on mobile. */}
        <div className="border-b border-border mb-6">
          <nav
            className="flex gap-1 overflow-x-auto -mb-px"
            aria-label="Settings sections"
          >
            {tabs.map((item) => {
              const active = isActive(item);
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => navigate(item.path)}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'group inline-flex items-center gap-2 whitespace-nowrap',
                    'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                    active
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  )}
                >
                  <Icon
                    className={cn(
                      'h-4 w-4',
                      active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                    )}
                  />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-6">{children}</div>
      </div>
    </EmployerLayout>
  );
}

export default SettingsLayout;
